import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useDeferredValue, useState } from 'react';

import type { Place } from './places';

/**
 * One of the Course, Module and Lesson filters: opened to browse, typed into to narrow
 * — ADMIN-16, generalised by ADMIN-18.
 *
 * The search runs on the server, so an option past the first hundred is still found.
 */
export function PlacePicker<T extends Place, Found>({
  label,
  value,
  width,
  search,
  lead,
  caption,
  onChange,
}: {
  label: string;
  value: T | null;
  width: number;
  search: (q: string) => {
    queryKey: readonly unknown[];
    queryFn: () => Promise<Found>;
    select: (found: Found) => T[];
    staleTime: number;
  };
  /** Offered above the results while the typed text does not rule it out. */
  lead?: T | undefined;
  /** A second line, for two options that share a title. */
  caption?: (one: T) => string | undefined;
  onChange: (next: T | null) => void;
}) {
  const [typed, setTyped] = useState('');
  // Nothing is fetched until the list is opened: three pickers asking on every visit
  // would be three requests for lists nobody looked at.
  const [open, setOpen] = useState(false);

  // The input shows the chosen title, so searching for it would hide every other option
  // the moment the list reopens.
  const q = useDeferredValue(
    value !== null && typed === value.title ? '' : typed.trim(),
  );
  const found = useQuery({
    ...search(q),
    enabled: open,
    placeholderData: keepPreviousData,
  });
  const leads =
    lead?.title.toLowerCase().includes(q.toLowerCase()) === true ? [lead] : [];

  return (
    <Autocomplete
      size="small"
      options={[...leads, ...(found.data ?? [])]}
      value={value}
      open={open}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
      }}
      inputValue={typed}
      loading={found.isFetching}
      filterOptions={(all) => all}
      getOptionLabel={(one) => one.title}
      isOptionEqualToValue={(one, other) => one.id === other.id}
      noOptionsText={
        q === ''
          ? `No ${label.toLowerCase()} yet.`
          : `No ${label.toLowerCase()} matches “${q}”.`
      }
      onInputChange={(_event, next) => {
        setTyped(next);
      }}
      onChange={(_event, next) => {
        onChange(next);
      }}
      // MUI keys an option by its label, and two courses can each have a "Foundations":
      // the shared key left a stale row in the list after the next search.
      renderOption={({ key: _byLabel, ...props }, one) => {
        const second = caption?.(one);

        return (
          <li key={one.id} {...props}>
            <span>
              {one.title}
              {second !== undefined && (
                <Typography
                  component="span"
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block' }}
                >
                  {second}
                </Typography>
              )}
            </span>
          </li>
        );
      }}
      renderInput={(params) => <TextField {...params} label={label} />}
      sx={{ minWidth: width }}
    />
  );
}
